import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface AppSettings {
  useMockData: boolean
}

interface SettingsContextType {
  settings: AppSettings
  updateSettings: (updates: Partial<AppSettings>) => void
}

const defaultSettings: AppSettings = {
  useMockData: false
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

const SETTINGS_STORAGE_KEY = 'sme-dashboard-settings'

// Global mock data flag - synced on app initialization
let globalUseMockData = false

export function setGlobalUseMockData(value: boolean) {
  globalUseMockData = value
}

export function getUseMockData(): boolean {
  return globalUseMockData
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
      if (stored) {
        const parsed = { ...defaultSettings, ...JSON.parse(stored) }
        // CRITICAL: Sync global mock flag on app initialization
        globalUseMockData = parsed.useMockData
        console.log(`[Settings] Initialized useMockData: ${parsed.useMockData}`)
        return parsed
      }
    } catch (e) {
      console.warn('Failed to load settings from localStorage:', e)
    }
    // Ensure global flag matches default
    globalUseMockData = defaultSettings.useMockData
    return defaultSettings
  })

  // Sync global flag whenever settings change
  useEffect(() => {
    globalUseMockData = settings.useMockData
    console.log(`[Settings] useMockData changed to: ${settings.useMockData}`)
  }, [settings.useMockData])

  // Persist settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
    } catch (e) {
      console.warn('Failed to save settings to localStorage:', e)
    }
  }, [settings])

  const updateSettings = (updates: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }))
  }

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
